import { useState } from "react";
import { Icon } from "../design-system/Icon";
import { useDateRange } from "./DateRangeContext";
import { PRESET_LABEL, formatRange, startOfDay, endOfDay, type PresetId } from "../lib/date-range";
import "./date-range-filter.css";

/**
 * Selector de rango de fechas reutilizable (Fase FF1). Un botón que abre presets rápidos (Hoy, Ayer,
 * 7/30 días, este mes, mes pasado) y un modo Personalizado con dos fechas. Incluye el interruptor de
 * comparación contra el período anterior. Lee y escribe el estado global (DateRangeContext), así que
 * cualquier pantalla que lo muestre queda sincronizada.
 */
const PRESETS: PresetId[] = ["today", "yesterday", "last7", "last30", "thisMonth", "lastMonth"];

/** yyyy-mm-dd local para <input type="date">. */
const toInput = (epoch: number): string => { const d = new Date(epoch); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const fromInput = (v: string): Date => { const [y, m, d] = v.split("-").map(Number); return new Date(y!, (m ?? 1) - 1, d ?? 1); };

export function DateRangeFilter(): JSX.Element {
  const { preset, range, compare, setPreset, setCustom, setCompare } = useDateRange();
  const [open, setOpen] = useState(false);

  const pickPreset = (p: PresetId): void => { setPreset(p); setOpen(false); };
  const setFrom = (v: string): void => setCustom({ fromEpoch: startOfDay(fromInput(v)), toEpoch: range.toEpoch });
  const setTo = (v: string): void => setCustom({ fromEpoch: range.fromEpoch, toEpoch: endOfDay(fromInput(v)) });

  return (
    <div className="drf">
      <button className="drf__trigger" onClick={() => setOpen((o) => !o)} aria-label="Filtrar por fechas" aria-expanded={open}>
        <Icon name="analytics" size={15} />
        <span className="drf__label">{preset === "custom" ? formatRange(range) : PRESET_LABEL[preset]}</span>
        <Icon name="arrow-down" size={13} />
      </button>

      {open ? (
        <>
          <div className="drf__scrim" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="drf__pop" role="dialog" aria-label="Rango de fechas">
            <div className="drf__presets">
              {PRESETS.map((p) => (
                <button key={p} className={`drf__preset${preset === p ? " drf__preset--on" : ""}`} onClick={() => pickPreset(p)}>
                  {PRESET_LABEL[p]}
                </button>
              ))}
            </div>
            <div className="drf__custom">
              <div className="drf__custom-title">Personalizado</div>
              <div className="drf__dates">
                <label>Desde<input type="date" value={toInput(range.fromEpoch)} onChange={(e) => setFrom(e.target.value)} /></label>
                <label>Hasta<input type="date" value={toInput(range.toEpoch)} onChange={(e) => setTo(e.target.value)} /></label>
              </div>
              <div className="drf__range-label">{formatRange(range)}</div>
            </div>
            <label className="drf__compare">
              <input type="checkbox" checked={compare} onChange={(e) => setCompare(e.target.checked)} />
              Comparar con período anterior
            </label>
          </div>
        </>
      ) : null}
    </div>
  );
}
